import type { AgentTool } from '@saaga/shared/interfaces/agent-tool.interface'
import { type ReactNode, useMemo, useState } from 'react'

import { Command, CommandInput } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { createToolFilter, filterToolBySearch } from './tool-selector.helper'
import { ToolSelectorCommandContent } from './tool-selector-command-content.atom'
import { ToolSelectorLoading } from './tool-selector-loading.atom'
import { ToolSelectorTrigger } from './tool-selector-trigger.atom'

export interface ToolSelectorSearchableProps {
  selectedIdList: string[]
  onToggle: (toolId: string) => void
  onSelectAll?: (filteredIdList: string[]) => void
  onDeselectAll?: (filteredIdList: string[]) => void
  toolList: AgentTool[]
  isLoading?: boolean
  placeholder?: string
  isDisabled?: boolean
  className?: string
}

/**
 * Searchable combobox for selecting tools to add to an agent node.
 * Supports multi-select with checkboxes and a "Select All" option.
 */
export const ToolSelectorSearchable = ({
  selectedIdList,
  onToggle,
  onSelectAll,
  onDeselectAll,
  toolList,
  isLoading = false,
  placeholder = 'Add a tool...',
  isDisabled = false,
  className,
}: ToolSelectorSearchableProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const filterFn = useMemo(() => createToolFilter(toolList), [toolList])

  const filteredToolList = useMemo(
    () => (search ? toolList.filter((tool) => filterToolBySearch(tool, search)) : toolList),
    [toolList, search]
  )

  if (isLoading) return <ToolSelectorLoading className={className} />

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <ToolSelectorTrigger
          placeholder={placeholder}
          isOpen={isOpen}
          isDisabled={isDisabled}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="tool-selector-content w-[var(--radix-popover-trigger-width)] p-0">
        <Command filter={filterFn}>
          <CommandInput
            placeholder="Search tools..."
            className="tool-selector-input h-9"
            onValueChange={setSearch}
          />
          <ToolSelectorCommandContent
            toolList={toolList}
            filteredToolList={filteredToolList}
            selectedIdList={selectedIdList}
            onToggle={onToggle}
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
          />
        </Command>
      </PopoverContent>
    </Popover>
  )
}

ToolSelectorSearchable.displayName = 'ToolSelectorSearchable'
